// DL Fork: date filter system types
/**
 * DL Date Filter System Types
 *
 * This module defines the comprehensive date filtering system for DataLink BI.
 * It supports:
 * - Main period selection (date range)
 * - Comparison periods (weekday aligned, same dates, custom)
 * - Quick dates (pre-defined periods from database)
 * - Date basis selection (which date column to filter on)
 * - Same Time Last Period cutoff for fair comparisons
 *
 * The values from this filter are passed through to PostgreSQL session variables
 * via the DL SQL proxy: app.current_start, app.current_end, app.compare_start,
 * app.compare_end, app.date_basis, app.cutoff_date
 */
import moment from 'moment-timezone';
/**
 * Pre-defined logic quick dates
 * These are computed dynamically and don't require database storage
 */
export const DL_LOGIC_QUICK_DATES = [
    {
        id: 'logic_today',
        name: 'Today',
        calculationType: 'today',
        description: 'Current day only',
        sortOrder: 1,
        isLogicDate: true,
    },
    {
        id: 'logic_yesterday',
        name: 'Yesterday',
        calculationType: 'yesterday',
        description: 'Previous day only',
        sortOrder: 2,
        isLogicDate: true,
    },
    {
        id: 'logic_last_7_days',
        name: 'Last 7 Days',
        calculationType: 'last_7_days',
        description: 'Rolling 7-day period with weekday-aligned comparison',
        sortOrder: 3,
        isLogicDate: true,
    },
    {
        id: 'logic_last_30_days',
        name: 'Last 30 Days',
        calculationType: 'last_30_days',
        description: 'Rolling 30-day period with weekday-aligned comparison',
        sortOrder: 4,
        isLogicDate: true,
    },
    {
        id: 'logic_this_week',
        name: 'This Week',
        calculationType: 'this_week',
        description: 'Current week (Monday to Sunday)',
        sortOrder: 5,
        isLogicDate: true,
    },
    {
        id: 'logic_this_month',
        name: 'This Month',
        calculationType: 'this_month',
        description: 'Current calendar month',
        sortOrder: 6,
        isLogicDate: true,
    },
    {
        id: 'logic_last_month',
        name: 'Last Month',
        calculationType: 'last_month',
        description: 'Previous calendar month',
        sortOrder: 7,
        isLogicDate: true,
    },
    {
        id: 'logic_last_quarter',
        name: 'Last Quarter',
        calculationType: 'last_quarter',
        description: 'Previous calendar quarter',
        sortOrder: 8,
        isLogicDate: true,
    },
    {
        id: 'logic_year_to_date',
        name: 'Year to Date',
        calculationType: 'year_to_date',
        description: 'January 1st to today',
        sortOrder: 9,
        isLogicDate: true,
    },
];
/**
 * Type guard to check if a quick date is a logic-computed date
 */
export function isLogicQuickDate(qd) {
    return 'isLogicDate' in qd && qd.isLogicDate === true;
}
/**
 * Pre-defined comparison mode options
 */
export const DL_COMPARISON_MODE_OPTIONS = [
    {
        value: 'weekday_aligned',
        title: 'Same time last year (weekday aligned)',
        description: '364 days prior - preserves day of week',
    },
    {
        value: 'same_dates',
        title: 'Same dates last year',
        description: 'Same calendar dates, previous year',
    },
    {
        value: 'custom',
        title: 'Custom comparison dates',
        description: 'Choose specific dates to compare',
    },
    {
        value: 'none',
        title: 'No comparison',
        description: 'Show main period data only',
    },
];
/**
 * Default date basis slug - matches PostgreSQL view expectations
 * Uses 'first_travel' (travel date) as the default basis
 */
export const DEFAULT_DATE_BASIS_SLUG = 'booking_travel';
/**
 * Helper to create default filter state
 * All values are initialized with defaults to ensure they're always passed to the proxy
 */
export function createDefaultDlDateFilterState() {
    const now = new Date();
    const thirtyDaysAgo = new Date(now);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    // Calculate default comparison dates (weekday aligned = 364 days prior)
    const compareEnd = new Date(now);
    compareEnd.setDate(compareEnd.getDate() - 364);
    const compareStart = new Date(thirtyDaysAgo);
    compareStart.setDate(compareStart.getDate() - 364);
    // Default STLP cutoff: "today" offset into the comparison period.
    // The cutoff is applied directly to comparison-period booking_created_date,
    // so we shift it back by the gap between main and comparison starts.
    const defaultCutoff = new Date(now);
    defaultCutoff.setDate(defaultCutoff.getDate() - 364);
    return {
        mainStart: thirtyDaysAgo,
        mainEnd: now,
        comparisonMode: 'weekday_aligned',
        compareStart,
        compareEnd,
        creationCutoffEnabled: true,
        mainCutoff: defaultCutoff, // Equivalent of "today" in the comparison period
        source: 'custom',
        dateBasisSlug: DEFAULT_DATE_BASIS_SLUG,
        // dateBasisName intentionally omitted - resolved from dateBasisOptions after they load
    };
}
/**
 * Helper to serialize filter state for API transport
 */
export function serializeDlDateFilterState(state) {
    return {
        mainStart: state.mainStart.toISOString(),
        mainEnd: state.mainEnd.toISOString(),
        comparisonMode: state.comparisonMode,
        compareStart: state.compareStart?.toISOString(),
        compareEnd: state.compareEnd?.toISOString(),
        creationCutoffEnabled: state.creationCutoffEnabled,
        mainCutoff: state.mainCutoff?.toISOString(),
        compareCutoff: state.compareCutoff?.toISOString(),
        source: state.source,
        quickDateUuid: state.quickDateUuid,
        quickDateName: state.quickDateName,
        dateBasisSlug: state.dateBasisSlug,
        dateBasisName: state.dateBasisName,
    };
}
/**
 * Helper to deserialize filter state from API
 */
export function deserializeDlDateFilterState(data) {
    return {
        mainStart: new Date(data.mainStart),
        mainEnd: new Date(data.mainEnd),
        comparisonMode: data.comparisonMode,
        compareStart: data.compareStart
            ? new Date(data.compareStart)
            : undefined,
        compareEnd: data.compareEnd ? new Date(data.compareEnd) : undefined,
        creationCutoffEnabled: data.creationCutoffEnabled,
        mainCutoff: data.mainCutoff ? new Date(data.mainCutoff) : undefined,
        compareCutoff: data.compareCutoff
            ? new Date(data.compareCutoff)
            : undefined,
        source: data.source,
        quickDateUuid: data.quickDateUuid,
        quickDateName: data.quickDateName,
        dateBasisSlug: data.dateBasisSlug,
        dateBasisName: data.dateBasisName,
    };
}
/**
 * Format a Date as YYYY-MM-DD in local time (avoids UTC timezone shift).
 * Using toISOString().split('T')[0] converts to UTC first, which shifts
 * dates back 1 day for users ahead of UTC (e.g., Australia UTC+10:30).
 */
function formatLocalDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}
/**
 * Convert filter state to params for SQL proxy
 * IMPORTANT: All values are always passed regardless of whether they've been changed.
 * This ensures the proxy always has complete context for PostgreSQL session variables.
 */
export function dlDateFilterStateToParams(state, timezone) {
    const formatDate = (date) => timezone && moment.tz.zone(timezone)
        ? moment(date).tz(timezone).format('YYYY-MM-DD')
        : formatLocalDate(date);
    const params = {
        current_start: formatDate(state.mainStart),
        current_end: formatDate(state.mainEnd),
        // Always pass date_basis - use default if not set
        date_basis: state.dateBasisSlug || DEFAULT_DATE_BASIS_SLUG,
    };
    // Always pass comparison dates if mode is not 'none'
    // Calculate them if not already set
    if (state.comparisonMode !== 'none') {
        if (state.compareStart && state.compareEnd) {
            params.compare_start = formatDate(state.compareStart);
            params.compare_end = formatDate(state.compareEnd);
        }
        else {
            // Calculate default comparison dates (364 days prior for weekday alignment)
            const compareEnd = new Date(state.mainEnd);
            compareEnd.setDate(compareEnd.getDate() - 364);
            const compareStart = new Date(state.mainStart);
            compareStart.setDate(compareStart.getDate() - 364);
            params.compare_start = formatDate(compareStart);
            params.compare_end = formatDate(compareEnd);
        }
    }
    // Only pass cutoff_date when cutoff is enabled
    // When omitted, the proxy sets DL_cutoff_enabled=0 and views skip cutoff filtering
    if (state.creationCutoffEnabled) {
        params.cutoff_date = formatDate(state.mainCutoff ?? new Date());
    }
    return params;
}
// DL Fork: resolve persisted dashboard defaults for non-browser consumers,
// including scheduled deliveries where relative dates use an explicit timezone.
export function resolveDlDateFilterDashboardConfig(config, quickDates, options = {}) {
    // DL Fork: a dashboard with no persisted plugin row still uses the date
    // picker's system defaults. Scheduled deliveries must resolve that same
    // state instead of treating the missing row as an unfiltered dashboard.
    const usesSystemDefaults = config == null;
    const effectiveConfig = config ?? {};
    const timezone = options.timezone && moment.tz.zone(options.timezone)
        ? options.timezone
        : 'UTC';
    const now = moment(options.now ?? new Date()).tz(timezone);
    const asCalendarDate = (value) => {
        const dateText = typeof value === 'string'
            ? value.slice(0, 10)
            : moment.utc(value).format('YYYY-MM-DD');
        const parsed = moment.tz(dateText, 'YYYY-MM-DD', true, timezone);
        return parsed.isValid() ? parsed.startOf('day') : null;
    };
    const defaultMainStart = now.clone().subtract(30, 'days').startOf('day');
    const defaultMainEnd = now.clone().endOf('day');
    let mainStart = defaultMainStart;
    let mainEnd = defaultMainEnd;
    let compareStart = defaultMainStart.clone().subtract(364, 'days');
    let compareEnd = defaultMainEnd.clone().subtract(364, 'days');
    let comparisonMode = 'weekday_aligned';
    let source = 'custom';
    let quickDateUuid;
    let quickDateName;
    const logicType = effectiveConfig.defaultLogicQuickDateType;
    if (logicType) {
        switch (logicType) {
            case 'today':
                mainStart = now.clone().startOf('day');
                mainEnd = now.clone().endOf('day');
                break;
            case 'yesterday':
                mainStart = now.clone().subtract(1, 'day').startOf('day');
                mainEnd = now.clone().subtract(1, 'day').endOf('day');
                break;
            case 'last_7_days':
                mainStart = now.clone().subtract(7, 'days').startOf('day');
                mainEnd = now.clone().subtract(1, 'day').endOf('day');
                break;
            case 'last_30_days':
                mainStart = now.clone().subtract(30, 'days').startOf('day');
                mainEnd = now.clone().subtract(1, 'day').endOf('day');
                break;
            case 'this_week':
                mainStart = now.clone().startOf('isoWeek');
                mainEnd = now.clone().endOf('isoWeek');
                break;
            case 'this_month':
                mainStart = now.clone().startOf('month');
                mainEnd = now.clone().endOf('month');
                break;
            case 'last_month':
                mainStart = now.clone().subtract(1, 'month').startOf('month');
                mainEnd = now.clone().subtract(1, 'month').endOf('month');
                break;
            case 'last_quarter':
                mainStart = now
                    .clone()
                    .subtract(1, 'quarter')
                    .startOf('quarter');
                mainEnd = now.clone().subtract(1, 'quarter').endOf('quarter');
                break;
            case 'year_to_date':
                mainStart = now.clone().startOf('year');
                mainEnd = now.clone().endOf('day');
                break;
            default:
                break;
        }
        compareStart = mainStart.clone().subtract(364, 'days');
        compareEnd = mainEnd.clone().subtract(364, 'days');
        source = 'quick_date';
        const logicQuickDate = DL_LOGIC_QUICK_DATES.find((item) => item.calculationType === logicType);
        quickDateUuid = logicQuickDate?.id;
        quickDateName = logicQuickDate?.name;
    }
    else if (effectiveConfig.defaultQuickDateUuid) {
        const quickDate = quickDates.find((item) => item.quickDateUuid === effectiveConfig.defaultQuickDateUuid);
        if (quickDate) {
            const resolvedMainStart = asCalendarDate(quickDate.mainStart);
            const resolvedMainEnd = asCalendarDate(quickDate.mainEnd);
            const resolvedCompareStart = asCalendarDate(quickDate.compareStart);
            const resolvedCompareEnd = asCalendarDate(quickDate.compareEnd);
            if (resolvedMainStart &&
                resolvedMainEnd &&
                resolvedCompareStart &&
                resolvedCompareEnd) {
                mainStart = resolvedMainStart;
                mainEnd = resolvedMainEnd.clone().endOf('day');
                compareStart = resolvedCompareStart;
                compareEnd = resolvedCompareEnd.clone().endOf('day');
                comparisonMode = 'custom';
                source = 'quick_date';
                quickDateUuid = quickDate.quickDateUuid;
                quickDateName = quickDate.name;
            }
        }
    }
    else if (effectiveConfig.defaultMainStart &&
        effectiveConfig.defaultMainEnd) {
        const resolvedMainStart = asCalendarDate(effectiveConfig.defaultMainStart);
        const resolvedMainEnd = asCalendarDate(effectiveConfig.defaultMainEnd);
        if (resolvedMainStart && resolvedMainEnd) {
            mainStart = resolvedMainStart;
            mainEnd = resolvedMainEnd.clone().endOf('day');
        }
    }
    if (effectiveConfig.defaultComparisonMode) {
        comparisonMode = effectiveConfig.defaultComparisonMode;
        if (comparisonMode === 'custom') {
            const resolvedCompareStart = effectiveConfig.defaultCompareStart
                ? asCalendarDate(effectiveConfig.defaultCompareStart)
                : null;
            const resolvedCompareEnd = effectiveConfig.defaultCompareEnd
                ? asCalendarDate(effectiveConfig.defaultCompareEnd)
                : null;
            if (resolvedCompareStart && resolvedCompareEnd) {
                compareStart = resolvedCompareStart;
                compareEnd = resolvedCompareEnd.clone().endOf('day');
            }
        }
        else if (comparisonMode === 'weekday_aligned') {
            compareStart = mainStart.clone().subtract(364, 'days');
            compareEnd = mainEnd.clone().subtract(364, 'days');
        }
        else if (comparisonMode === 'same_dates') {
            compareStart = mainStart.clone().subtract(1, 'year');
            compareEnd = mainEnd.clone().subtract(1, 'year');
        }
    }
    const hiddenDateBasisSlugs = Array.isArray(effectiveConfig.hiddenDateBasisSlugs)
        ? effectiveConfig.hiddenDateBasisSlugs
        : [];
    const dateBasisSlug = effectiveConfig.defaultDateBasisSlug &&
        !hiddenDateBasisSlugs.includes(effectiveConfig.defaultDateBasisSlug)
        ? effectiveConfig.defaultDateBasisSlug
        : DEFAULT_DATE_BASIS_SLUG;
    const cutoffDate = effectiveConfig.creationCutoffEnabled &&
        effectiveConfig.defaultCutoffDate
        ? asCalendarDate(effectiveConfig.defaultCutoffDate)
        : usesSystemDefaults
            ? now.clone().subtract(364, 'days').startOf('day')
            : effectiveConfig.creationCutoffEnabled
                ? now.clone().startOf('day')
                : null;
    return {
        mainStart: mainStart.toDate(),
        mainEnd: mainEnd.toDate(),
        comparisonMode,
        ...(comparisonMode !== 'none' && {
            compareStart: compareStart.toDate(),
            compareEnd: compareEnd.toDate(),
        }),
        creationCutoffEnabled: usesSystemDefaults ||
            effectiveConfig.creationCutoffEnabled === true,
        ...(cutoffDate && { mainCutoff: cutoffDate.toDate() }),
        source,
        ...(quickDateUuid && { quickDateUuid }),
        ...(quickDateName && { quickDateName }),
        dateBasisSlug,
    };
}
export function resolveDlDateFilterDashboardConfigToParams(config, quickDates, options = {}) {
    const timezone = options.timezone && moment.tz.zone(options.timezone)
        ? options.timezone
        : 'UTC';
    return dlDateFilterStateToParams(resolveDlDateFilterDashboardConfig(config, quickDates, {
        ...options,
        timezone,
    }), timezone);
}
//# sourceMappingURL=dlDateFilter.js.map